import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, List, Card, Tag, Typography, Space, Empty, Divider, Alert, message, Button, Popconfirm } from 'antd';
import { 
    BulbOutlined, 
    LinkOutlined, 
    UserOutlined, 
    PictureOutlined, 
    EnvironmentOutlined,
    FieldTimeOutlined,
    SyncOutlined,
    DeleteOutlined,
    PlusOutlined
} from '@ant-design/icons';
import { SourcePanel, SourceProcess, SourceScene } from '../../types';
import { sourceStoriesApi } from '../../services/api';
import ManualReferenceModal from './ManualReferenceModal';
import { useApi } from '../../hooks';
import { visualPlanRefPreviewUrls } from '../../utils/panelReferenceImages';

const { Text, Title, Paragraph } = Typography;

interface ReferenceItem {
    types: ('IDENTITY' | 'OUTFIT' | 'ENVIRONMENT' | 'LIGHTING' | 'OTHER')[];
    type?: 'IDENTITY' | 'OUTFIT' | 'ENVIRONMENT' | 'LIGHTING' | 'OTHER'; // Backward compatibility
    sourceId: string;
    sourceUrl: string;
    sourceUrls?: string[];
    label: string;
    reason: string;
}

interface VisualReferencePlan {
    temporalStatus: 'CONTINUOUS' | 'TIME_SKIP';
    planningLogic: string;
    references: ReferenceItem[];
}

interface VisualReferencePlanModalProps {
    visible: boolean;
    panel: SourcePanel | null;
    storyId: string;
    onClose: () => void;
}

const VisualReferencePlanModal: React.FC<VisualReferencePlanModalProps> = ({
    visible,
    panel,
    storyId,
    onClose
}) => {
    const [plan, setPlan] = useState<VisualReferencePlan | null>(null);
    const [loading, setLoading] = useState(false);

    // For manual reference selection
    const [isManualRefVisible, setIsManualRefVisible] = useState(false);
    const [isAddingRef, setIsAddingRef] = useState(false);

    // Fetch all hierarchy data for panel picker
    const { data: processesData } = useApi(useCallback(() => sourceStoriesApi.getSourceProcesses(storyId), [storyId]), { immediate: visible });
    const { data: scenesData } = useApi(useCallback(() => sourceStoriesApi.getAllSourceScenes(storyId), [storyId]), { immediate: visible });
    const { data: panelsData } = useApi(useCallback(() => sourceStoriesApi.getAllSourcePanels(storyId), [storyId]), { immediate: visible });

    // Calculate default process and scene for manual reference (suggest previous scene)
    const previousSceneContext = useMemo(() => {
        if (!visible || !panel || !processesData?.data || !scenesData?.data) return null;

        const processes = [...processesData.data].sort((a, b) => a.orderIndex - b.orderIndex);
        const scenes = [...scenesData.data].sort((a, b) => a.orderIndex - b.orderIndex);

        const currentScene = scenes.find(s => s.id === panel.sceneId);
        if (!currentScene) return null;

        const currentProcess = processes.find(p => p.id === currentScene.processId);
        if (!currentProcess) return null;

        // Find scenes in current process
        const processScenes = scenes.filter(s => s.processId === currentProcess.id);
        const currentSceneIdx = processScenes.findIndex(s => s.id === currentScene.id);

        if (currentSceneIdx > 0) {
            // Previous scene in same process
            return {
                processId: currentProcess.id,
                sceneId: processScenes[currentSceneIdx - 1].id
            };
        } else {
            // First scene in process, go to previous process
            const currentProcessIdx = processes.findIndex(p => p.id === currentProcess.id);
            if (currentProcessIdx > 0) {
                const prevProcess = processes[currentProcessIdx - 1];
                const prevProcessScenes = scenes.filter(s => s.processId === prevProcess.id);
                if (prevProcessScenes.length > 0) {
                    return {
                        processId: prevProcess.id,
                        sceneId: prevProcessScenes[prevProcessScenes.length - 1].id
                    };
                }
            }
        }

        // Default to current if no previous found
        return {
            processId: panel.processId,
            sceneId: panel.sceneId
        };
    }, [visible, panel, processesData, scenesData]);

    const fetchPlan = useCallback(async () => {
        if (!panel) return;
        
        // If panel already has a plan, use it
        if (panel.visualPlan) {
            setPlan(panel.visualPlan);
            return;
        }

        // Otherwise fetch/generate from AI
        setLoading(true);
        try {
            const res = await sourceStoriesApi.checkVisualReferences(storyId, panel.sceneId, panel.id);
            setPlan(res.data);
        } catch (error) {
            console.error('Lỗi khi lập kế hoạch tham chiếu:', error);
            message.error('Lỗi khi lập kế hoạch tham chiếu bằng AI');
        } finally {
            setLoading(false);
        }
    }, [panel, storyId]);

    useEffect(() => {
        if (visible && panel) {
            fetchPlan();
        } else if (!visible) {
            setPlan(null);
        }
    }, [visible, panel, fetchPlan]);

    const handleRemoveRef = async (sourceId: string) => {
        if (!panel) return;
        try {
            const res = await sourceStoriesApi.removeReference(storyId, panel.sceneId, panel.id, sourceId);
            setPlan(res.data);
            message.success('Đã xóa tham chiếu!');
        } catch (error) {
            message.error('Lỗi khi xóa tham chiếu');
        }
    };

    const handleAddManualRef = async (sourcePanelId: string) => {
        if (!panel) return;
        setIsAddingRef(true);
        try {
            const res = await sourceStoriesApi.addManualReference(storyId, panel.sceneId, panel.id, sourcePanelId);
            setPlan(res.data);
            message.success('Đã thêm và phân tích tham chiếu mới!');
            setIsManualRefVisible(false); // Close modal after success
        } catch (error) {
            message.error('Lỗi khi thêm tham chiếu thủ công');
        } finally {
            setIsAddingRef(false);
        }
    };

    const getReferenceIcon = (type: string) => {
        switch (type) {
            case 'IDENTITY': return <UserOutlined />;
            case 'OUTFIT': return <PictureOutlined />;
            case 'ENVIRONMENT': return <EnvironmentOutlined />;
            default: return <LinkOutlined />;
        }
    };

    const getReferenceColor = (type: string) => {
        switch (type) {
            case 'IDENTITY': return 'blue';
            case 'OUTFIT': return 'purple';
            case 'ENVIRONMENT': return 'green';
            default: return 'default';
        }
    };

    const sortedReferences = plan?.references ? [...plan.references].sort((a, b) => {
        const aTypes = a.types || (a.type ? [a.type] : []);
        const bTypes = b.types || (b.type ? [b.type] : []);
        
        const aIsIdentity = aTypes.includes('IDENTITY');
        const bIsIdentity = bTypes.includes('IDENTITY');

        // AI references first, Identity last
        if (aIsIdentity && !bIsIdentity) return 1;
        if (!aIsIdentity && bIsIdentity) return -1;
        return 0;
    }) : [];

    return (
        <Modal
            title={
                <Space>
                    <BulbOutlined style={{ color: '#faad14' }} />
                    <span>Kế hoạch tham chiếu hình ảnh - Panel #{panel?.sceneOrderIndex}</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={[
                <Button key="close" onClick={onClose}>Đóng</Button>,
                <Button 
                    key="add" 
                    icon={<PlusOutlined />} 
                    onClick={() => setIsManualRefVisible(true)}
                >
                    Thêm tham chiếu thủ công
                </Button>,
                <Button 
                    key="refresh" 
                    icon={<SyncOutlined />} 
                    onClick={() => {
                        setPlan(null);
                        setLoading(true);
                        sourceStoriesApi.checkVisualReferences(storyId, panel!.sceneId, panel!.id)
                            .then(res => setPlan(res.data))
                            .catch(() => message.error('Lỗi khi tạo lại kế hoạch'))
                            .finally(() => setLoading(false));
                    }}
                    loading={loading}
                >
                    Tạo lại toàn bộ (AI)
                </Button>
            ]}
            width={900}
            centered
            loading={loading && !plan}
            styles={{
                body: {
                    maxHeight: 'calc(100vh - 200px)',
                    overflowY: 'auto',
                    padding: '24px'
                }
            }}
        >
            {plan ? (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '8px' }}>
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text strong>Trạng thái thời gian:</Text>
                                <Tag 
                                    icon={<FieldTimeOutlined />} 
                                    color={plan.temporalStatus === 'CONTINUOUS' ? 'cyan' : 'orange'}
                                >
                                    {plan.temporalStatus === 'CONTINUOUS' ? 'TIẾP NỐI TỨC THÌ' : 'BƯỚC NHẢY THỜI GIAN'}
                                </Tag>
                            </div>
                            <Divider style={{ margin: '8px 0' }} />
                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 4 }}>Logic lập kế hoạch:</Text>
                                <Paragraph style={{ margin: 0, fontSize: '13px' }} italic>
                                    "{plan.planningLogic}"
                                </Paragraph>
                            </div>
                        </Space>
                    </div>

                    <div>
                        <Title level={5}>Danh sách nguyên liệu tham chiếu:</Title>
                        <List
                            dataSource={sortedReferences}
                            renderItem={(item) => {
                                const itemTypes = item.types || (item.type ? [item.type] : []);
                                const primaryType = itemTypes[0] || 'OTHER';
                                const isIdentity = itemTypes.includes('IDENTITY');
                                const thumbs = visualPlanRefPreviewUrls(item);
                                
                                return (
                                    <List.Item style={{ padding: '4px 0' }}>
                                        <Card 
                                            size="small" 
                                            hoverable 
                                            style={{ width: '100%', borderLeft: `4px solid ${getReferenceColor(primaryType)}` }}
                                            bodyStyle={{ padding: '8px 12px' }}
                                        >
                                            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                                <div style={{ 
                                                    display: 'flex',
                                                    gap: 3,
                                                    width: thumbs.length > 1 ? 68 : 60,
                                                    height: 106, 
                                                    flexShrink: 0, 
                                                    background: '#f0f0f0',
                                                    borderRadius: 4,
                                                    overflow: 'hidden',
                                                    border: '1px solid #d9d9d9'
                                                }}>
                                                    {thumbs.length > 0 ? (
                                                        thumbs.map((u, ti) => (
                                                            <img 
                                                                key={ti}
                                                                src={u} 
                                                                alt={item.label} 
                                                                style={{ flex: 1, minWidth: 0, height: '100%', objectFit: 'cover' }} 
                                                            />
                                                        ))
                                                    ) : (
                                                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <PictureOutlined style={{ fontSize: 20, color: '#bfbfbf' }} />
                                                        </div>
                                                    )}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                                                        <div>
                                                            {itemTypes.map(t => (
                                                                <Tag key={t} icon={getReferenceIcon(t)} color={getReferenceColor(t)}>
                                                                    {t}
                                                                </Tag>
                                                            ))}
                                                            <Text strong style={{ fontSize: '13px', marginLeft: 4 }}>{item.label}</Text>
                                                        </div>
                                                        {!isIdentity && (
                                                            <Popconfirm
                                                                title="Xóa tham chiếu này?"
                                                                onConfirm={() => handleRemoveRef(item.sourceId)}
                                                                okText="Xóa"
                                                                cancelText="Hủy"
                                                            >
                                                                <Button 
                                                                    size="small" 
                                                                    type="text" 
                                                                    danger 
                                                                    icon={<DeleteOutlined />} 
                                                                />
                                                            </Popconfirm>
                                                        )}
                                                    </div>
                                                    <div style={{ background: '#fafafa', padding: '6px 10px', borderRadius: '4px', border: '1px solid #f0f0f0' }}>
                                                        <Paragraph 
                                                            style={{ fontSize: '12.5px', color: '#444', margin: 0, lineHeight: '1.4', whiteSpace: 'pre-wrap' }}
                                                        >
                                                            {item.reason}
                                                        </Paragraph>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </List.Item>
                                );
                            }}
                        />
                    </div>
                </Space>
            ) : !loading && (
                <Empty description="Không có dữ liệu kế hoạch tham chiếu" />
            )}

            {isManualRefVisible && (
                <ManualReferenceModal
                    visible={isManualRefVisible}
                    onClose={() => setIsManualRefVisible(false)}
                    onSelect={handleAddManualRef}
                    processes={processesData?.data || []}
                    scenes={scenesData?.data || []}
                    panels={panelsData?.data || []}
                    loading={isAddingRef}
                    defaultProcessId={previousSceneContext?.processId || panel?.processId}
                    defaultSceneId={previousSceneContext?.sceneId || panel?.sceneId}
                />
            )}
        </Modal>
    );
};

export default VisualReferencePlanModal;
